<template>
  <span id="start-day-section" v-if="this.$can(this.permissions.WAREHOUSE.EDIT_SETTINGS)">
    <el-button
      id="btn-start-day"
      type="success"
      size="mini"
      plain
      @click="dialogVisible = true"
    >{{ $t('warehouse.startDay.title') }}</el-button>
    <el-dialog
      width="65%"
      class="dialog-with-cols"
      :close-on-click-modal="false"
      :title="$t('warehouse.startDay.title')"
      :visible.sync="dialogVisible"
      @open="handleOpen()"
    >
      <el-form :model="form" :rules="formRules" ref="form" label-position="top" size="mini">
        <el-row>
          <el-col :md="8" :xs="12" class="form-vertical-item">
            <el-form-item class="inline-input" :label="$t('warehouse.startDay.warehouse')">
              <el-input class="inline-input" disabled :value="user.warehouseCode"></el-input>
            </el-form-item>
          </el-col>
          <el-col :md="8" :xs="12" class="form-vertical-item">
            <el-form-item
              :label="$t('warehouse.startDay.startDate')"
              prop="startDate"
              class="inline-input"
            >
              <el-date-picker
                class="el-dropdown-new drop-sp date-dob"
                v-model="form.startDate"
                format="MM/dd/yyyy"
                type="date"
                :picker-options="warehouse.datePickerOptions"
                :clearable="false"
                @change="handleSearch()"
              ></el-date-picker>
            </el-form-item>
          </el-col>
          <el-col :md="8" :xs="12" class="form-vertical-item">
            <el-form-item :label="$t('warehouse.startDay.layout')" prop="layoutId" ref="layoutId">
              <el-select
                v-model="form.layoutId"
                :no-data-text="noLayoutForDateMessage"
              >
                <el-option
                  v-for="item in layouts"
                  :key="item.id"
                  :label="layoutName(item)"
                  :value="item.id"
                ></el-option>
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row>
          <el-col :md="8" :xs="12" class="form-vertical-item">
            <el-form-item
              :label="$t('warehouse.startDay.loadingSpeed')"
              prop="loadingSpeed"
              ref="loadingSpeed"
              class="inline-input"
            >
              <el-input
                class="inline-input"
                ref="loadingSpeed"
                v-model="form.loadingSpeed"
                v-numeric-validation
                @focus="$event.target.select()"
              ></el-input>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      <div id="start-day-dialog-footer" class="dialog-footer form-vertical-item flex-reverse-direction" slot="footer">
        <el-button
          id="form-save-button"
          type="primary"
          size="mini"
          @click="submitForm()"
        >{{ $t('warehouse.startDay.title') }}</el-button>
        <el-button id="form-cancel-button" size="mini" @click="resetForm()">{{ $t('common.clear') }}</el-button>
      </div>
    </el-dialog>
  </span>
</template>

<script>
import { mapGetters } from 'vuex';
import moment from 'moment';
import { Message } from 'element-ui';
import constants from '@/utils/constants';
import layoutService from '@/api/layoutService';

export default {
  name: 'StartDay',
  computed: {
    ...mapGetters(['user', 'warehouse']),
    dateDisplayFormat() {
      return constants.DATES.DEFAULT_DISPLAY_FORMAT;
    },
    minLoadingSpeed() {
      return constants.WAREHOUSE_SCHEDULE_SETTINGS.MIN_LOADING_SPEED;
    },
    maxLoadingSpeed() {
      return constants.WAREHOUSE_SCHEDULE_SETTINGS.MAX_LOADING_SPEED;
    },
    noLayoutForDateMessage() {
      return this.$t('warehouse.startDay.noLayoutForDate').replace('[date]', moment(this.form.startDate).format(this.dateDisplayFormat));
    }
  },
  data() {
    const validateLoadingSpeed = (rule, value, callback) => {
      if (!value && this.editForm.cutOff) {
        callback(new Error(this.$t('warehouse.settings.error.loadingSpeedRequired')));
      } else if (!Number.isInteger(Number(value)) || Number(value) < this.minLoadingSpeed || Number(value) > this.maxLoadingSpeed) {
        callback(new Error(this.$t('warehouse.settings.error.loadingSpeedValid').replace('[minValue]', this.minLoadingSpeed).replace('[maxValue]', this.maxLoadingSpeed)));
      } else {
        callback();
      }
    };
    return {
      dialogVisible: false,
      layouts: [],
      form: {
        warehouseId: null,
        startDate: new Date(),
        layoutId: null,
        loadingSpeed: null
      },
      formRules: {
        startDate: [
          { required: true, message: this.$t('warehouse.startDay.error.dateRequired'), trigger: 'blur' }
        ],
        layoutId: [
          { required: true, message: this.$t('warehouse.startDay.error.layoutRequired'), trigger: 'blur' }
        ],
        loadingSpeed: [
          { validator: validateLoadingSpeed, trigger: 'blur' },
          { required: true, message: this.$t('warehouse.startDay.error.loadingSpeedRequired'), trigger: 'blur' }
        ]
      }
    };
  },
  methods: {
    handleOpen() {
      this.$nextTick(() => {
        this.resetForm();
      });
    },
    resetForm() {
      this.$refs.form.resetFields();
      this.form.warehouseId = this.user.warehouseId;
      this.form.startDate = new Date();
      this.handleSearch();
    },
    async handleSearch() {
      this.$refs.layoutId.resetField();
      this.form.layoutId = null;
      this.$refs.loadingSpeed.resetField();
      const day = moment(this.form.startDate.getTime()).day();
      const daySchedule = this.warehouse.detail.warehouseSchedule.find(schedule => schedule.dayNumber === day);
      if (daySchedule) {
        this.form.loadingSpeed = daySchedule.loadingSpeed;
      }
      const response = await layoutService.search({ warehouseId: this.form.warehouseId, layoutDateFrom: this.form.startDate, layoutDateTo: this.form.startDate });
      if (response && response.data) {
        this.layouts = response.data;
      }
    },
    async submitForm() {
      const day = moment(this.form.startDate.getTime()).day();
      const daySchedule = this.warehouse.detail.warehouseSchedule.find(schedule => schedule.dayNumber === day);
      if (!daySchedule || !daySchedule.dataEntryCutOff) {
        const dayName = constants.DAYS_OPTIONS.find(option => option.value === day.toString()).label;
        this.$message.error(this.$t('warehouse.startDay.error.dataEntryCutOffNotConfigured').replace('[dayOfWeek]', dayName));
        return;
      }
      this.$refs.form.validate(async (valid) => {
        if (valid) {
          if (moment(Date.now()).isBefore(this.form.startDate, 'day')) {
            const confirmStartDay = await this.$confirm(this.$t('warehouse.startDay.confirmStartDay').replace('[date]', moment(this.form.startDate).format(this.dateDisplayFormat)), { confirmButtonText: this.$t('warehouse.startDay.title') }).catch(() => false);
            if (!confirmStartDay) {
              return;
            }
          }
          Message.closeAll();
          const loading = this.$loading(constants.LOADING.DEFAULT_CONFIG);
          try {
            const response = await this.$store.dispatch('warehouse/startDay', this.form);
            this.$message.success(response.message);
            this.resetForm();
          } finally {
            loading.close();
          }
        }
      });
    },
    layoutName(layout) {
      return moment(layout.layoutDate).format(constants.DATES.DEFAULT_DISPLAY_FORMAT)
        .concat(' ')
        .concat(layout.dayName)
        .concat(' - ')
        .concat(layout.code)
        .concat(' - ')
        .concat(layout.name);
    }
  }
};
</script>

<style rel="stylesheet/scss" lang="scss">
#start-day-section {
  .el-select, .el-date-editor, .el-input-number {
    width: 100%;
  }

  .el-form-item--mini .el-form-item__error {
     -ms-word-break: break-all;
     word-break: break-all;

     /* Non standard for WebKit */
     word-break: break-word;

    -webkit-hyphens: auto;
    -moz-hyphens: auto;
    hyphens: auto;
  }
}
</style>
